import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { NOOR } from "@/constants/testIds/noor";
import { fadeUp, trans } from "./motion";
import useFormSubmit from "./useFormSubmit";
import StatusMessage from "./StatusMessage";

const BG_GLOW = {
  background: "radial-gradient(ellipse 50% 80% at 50% 50%, rgba(230,184,162,0.10), transparent 60%)",
};

const SECTION_TRANS = trans(1);

function ReserveInput({ email, onChange, disabled }) {
  return (
    <input
      data-testid={NOOR.reserveEmailInput}
      type="email"
      required
      placeholder="your@address.com"
      value={email}
      onChange={onChange}
      className="input-line text-center sm:text-left"
      disabled={disabled}
    />
  );
}

function ReserveSubmit({ loading }) {
  return (
    <button
      data-testid={NOOR.reserveSubmitBtn}
      type="submit"
      className="btn-solid whitespace-nowrap"
      disabled={loading}
    >
      {loading ? "Requesting…" : "Request a bottle from Batch 001"}
      <ArrowRight size={14} />
    </button>
  );
}

function ReserveForm() {
  const [email, setEmail] = useState("");
  const { status, errMsg, submit, reset } = useFormSubmit("/reserve");

  const onChange = (e) => {
    setEmail(e.target.value);
    if (status !== "idle" && status !== "loading") reset();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const ok = await submit({ email });
    if (ok) setEmail("");
  };

  return (
    <form onSubmit={onSubmit} className="mt-12 max-w-xl mx-auto liquid-glass p-6 md:p-8 rounded-sm">
      <div className="flex flex-col sm:flex-row items-stretch gap-4">
        <ReserveInput email={email} onChange={onChange} disabled={status === "loading"} />
        <ReserveSubmit loading={status === "loading"} />
      </div>
      <StatusMessage
        status={status}
        errMsg={errMsg}
        successText="Your name is inscribed. Watch your inbox."
        successTestId={NOOR.reserveSuccess}
      />
    </form>
  );
}

export default function Reserve() {
  return (
    <section
      data-testid={NOOR.reserveSection}
      id="reserve"
      className="relative py-32 md:py-44 overflow-hidden"
      style={{ background: "var(--bg-surface)" }}
    >
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={BG_GLOW} />
      <div className="relative max-w-3xl mx-auto px-6 md:px-10 text-center">
        <p className="overline mb-6">V · Reserve</p>
        <motion.h2
          {...fadeUp}
          transition={SECTION_TRANS}
          className="font-display text-4xl md:text-6xl leading-[1.05]"
          style={{ color: "var(--text-primary)" }}
        >
          The next batch of six is <br />
          <em className="italic" style={{ color: "var(--accent-rose)" }}>waiting to be claimed</em>.
        </motion.h2>
        <p className="mt-6 text-base md:text-lg max-w-xl mx-auto font-light" style={{ color: "var(--text-secondary)" }}>
          Leave your address and we shall whisper to you when the harvest crystallises.
          No public release — invitation only.
        </p>
        <ReserveForm />
      </div>
    </section>
  );
}
